import { ServerInferRequest } from "@ts-rest/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getBackendAPIClient } from "../lib/backendAPIClient";
import { financialDocumentContract } from "../common/contracts/financial-document.contract";
import { FinancialDocumentDto } from "../common/dto/financialDocument/financial-document.dto";
import { FinancialDocument } from "../common/types/financialDocument";
import { entitiesAccountsQueryKey } from "../queries/entitiesAccounts";

const financialDocumentContractClient = getBackendAPIClient(
    financialDocumentContract,
);

export function useCreateUploadUrlMutation() {
    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialDocumentContract.createUploadUrl
            >["body"],
        ) => {
            const result =
                await financialDocumentContractClient.createUploadUrl({ body });

            return result.body.url;
        },
    });
}

export function useProcessS3Upload(workspaceId: string) {
    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialDocumentContract.uploadDocumentS3
            >["body"],
        ) => {
            const result =
                await financialDocumentContractClient.uploadDocumentS3({
                    body: {
                        ...body,
                        workspaceId,
                    },
                });

            return result.body.map(FinancialDocumentDto.deserialize);
        },
    });
}

export function useUpdateFinancialDocumentMetadataMutation(
    financialDocument: FinancialDocument,
    onSuccess?: (document: FinancialDocument) => void,
) {
    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialDocumentContract.updateDocumentMetadata
            >["body"],
        ) => {
            const result =
                await financialDocumentContractClient.updateDocumentMetadata({
                    body,
                    params: {
                        financialDocumentId: financialDocument.id.toString(),
                    },
                });

            return FinancialDocumentDto.deserialize(result.body);
        },
        onSuccess,
    });
}

export function useFinancialDocumentReviewMutation(
    document: FinancialDocument,
    onSuccess?: (document: FinancialDocument) => void,
) {
    return useMutation({
        mutationFn: async () => {
            const result = await financialDocumentContractClient.markAsReviewed(
                {
                    params: {
                        financialDocumentId: document.id.toString(),
                    },
                },
            );

            return FinancialDocumentDto.deserialize(result.body);
        },
        onSuccess,
    });
}

export function useFinancialDocumentFinalizeTransactionsImportMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (financialDocumentId: number) => {
            await financialDocumentContractClient.finalizeTransactionsImport({
                params: {
                    financialDocumentId: financialDocumentId.toString(),
                },
            });
        },
        onSuccess: async () => {
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
        },
    });
}
